import { type FunctionComponent } from 'react';
import { TrackedEvent } from '../../../helpers/analytics/event-types';
import { trackSearchEvent } from '../../../helpers/search-helper/search-analytics.helper';
import { useSearchResults, type UseSearchResultsPayload } from '../../../hooks/apollo/search/search.hooks';
import { TextButton } from '../../buttons';

export type NoFilteredSearchResultsProps = {
	className?: string;
	HeadingTag?: Extract<keyof HTMLElementTagNameMap, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>;
	headingClass?: string;
	sameLine?: boolean;
	useResults?: () => UseSearchResultsPayload;
};

export const NoFilteredSearchResults: FunctionComponent<NoFilteredSearchResultsProps> = ({
	className = 'ml3-l mb6',
	HeadingTag = 'h2',
	headingClass = '',
	sameLine = false,
	useResults = useSearchResults
}) => {
	const { clearFacets, query, results, createGTMEvent } = useResults();
	const selectedFacetGroups = results?.selectedFacetGroups ?? [];
	const clearAllFacets = () => {
		selectedFacetGroups.forEach((group) => {
			group.facets.forEach((facet) => {
				if (!facet.autoApplied) {
					void trackSearchEvent(createGTMEvent(TrackedEvent.FACET_INTERACTION, { query, facet, group, applied: false }));
				}
			});
		});
		clearFacets();
	};
	const headingContent = 'No results with current filters';
	const bodyContent: JSX.Element = (
		<>
			You may find more results if you <TextButton onClick={clearAllFacets}>clear all filters</TextButton>, or remove filters
			individually.
		</>
	);

	return (
		<div className={className} data-automation="no-filtered-results" data-testid="no-filtered-results">
			{sameLine ? (
				<div className="normal mt3">
					{headingContent}. {bodyContent}
				</div>
			) : (
				<>
					<HeadingTag className={headingClass}>{headingContent}</HeadingTag>
					<div>{bodyContent}</div>
				</>
			)}
		</div>
	);
};
